import { useState } from 'react'
import { Meteors } from '@stianlarsen/meteors'
import { logExperienceTabClick } from '../../supabaseTracker'
import './Experience.css'

const experiences = [
  {
    id: 'ridenear',
    company: 'RideNear',
    role: 'Software Engineer',
    period: 'Jul 2025 — Present',
    type: 'Full-Time',
    location: 'Remote',
    description:
      'Building and scaling a multi-provider ride-hailing platform serving B2B and B2C users across multiple markets. I ship features end-to-end across frontend and backend with a strong focus on security, reliability, and product impact.',
    responsibilities: [
      'Implemented a comprehensive RBAC system with 45 permissions across microservices (API guards + PermissionGate components)',
      'Built company tiers + feature flags for safe rollouts and SaaS monetization with multi-tenant data isolation',
      'Refactored payment analytics from raw SQL to Prisma ORM while preserving complex aggregation for graphs + ride statistics',
      'Developed real-time chat integrated with Firebase Cloud Messaging (FCM token management + background notifications)',
      'Built a CMS with rich text editor for news, careers, FAQs, and legal docs (reduced developer time for content updates by ~90%)',
    ],
    stack: ['TypeScript', 'Next.js', 'NestJS', 'PostgreSQL', 'Prisma', 'Firebase', 'Docker', 'AWS'],
    accentColor: '#2978b5',
    logoSrc: '/images/cards/ridenear.png',
  },
  {
    id: 'gsoc-c2si',
    company: 'Google Summer of Code (C2SI)',
    role: 'GSoC Contributor — B0Bot',
    period: 'May 2026 — Aug 2026',
    type: 'Open Source',
    location: 'Remote',
    description:
      'Modernized B0Bot, a cybersecurity intelligence platform, from a monolith to a production-focused multi-service architecture with ingestion, API, and notifications.',
    responsibilities: [
      'Split legacy Flask monolith into api-service, ingestion-service, and notification-service with Docker Compose',
      'Built RSS ingestion + deduplication pipeline with queue-based processing and idempotent job handling',
      'Integrated embeddings (all-MiniLM-L6-v2) and pgvector retrieval in PostgreSQL for semantic search readiness',
      'Improved reliability with pytest coverage and notification digest safeguards including duplicate-send prevention',
    ],
    stack: ['Python', 'Flask', 'PostgreSQL', 'pgvector', 'Redis', 'Docker Compose', 'pytest'],
    accentColor: '#ef4444',
    logoSrc: '/images/cards/gsoc.png',
  },
  {
    id: 'turing',
    company: 'Turing',
    role: 'LLM Trainer',
    period: 'Sep 2025 — Oct 2025',
    type: 'Contract',
    location: 'Remote',
    description:
      'Short-term contract focused on LLM evaluation pipelines — creating model-breaking coding problems (similar to Codeforces) and improving review throughput with automation.',
    responsibilities: [
      'Designed and authored hard coding problems to stress-test model reasoning and correctness',
      'Introduced AI-powered pre-review via GitHub Actions to reduce manual reviewer effort',
      'Built internal agents to generate + validate problems before human review',
      'Improved iteration speed and consistency of the evaluation workflow',
    ],
    stack: ['TypeScript', 'Python', 'GitHub Actions', 'LLM Evaluation', 'Automation'],
    accentColor: '#22c55e',
    logoSrc: '/images/cards/turing.png',
  },
  {
    id: 'upwork',
    company: 'Upwork',
    role: 'Freelance Full-Stack Engineer',
    period: 'Jun 2023 — Dec 2024',
    type: 'Freelance',
    location: 'Global',
    description:
      'Delivered 25+ end-to-end software projects for startups and established brands across fintech, SaaS, e-commerce, and education. Specialized in TypeScript full-stack development, AI integrations, and scalable architecture.',
    responsibilities: [
      'Shipped production apps from concept → deployment with clean architecture and maintainable code',
      'Built P&L App (5K+ downloads): Kotlin native module (Accessibility APIs) + SnapTrade auto-sync for 20+ brokerages',
      'Built QuickStart AI: no-code RAG chatbot platform with hybrid search and citation tracking',
      'Built R&B Tea USA franchise platform (1,500+ locations): real-time chat + onboarding workflows',
      'Open source impact: fixed critical bugs in Expensify (15M+ users), $500 bounty; contributed 13+ PRs to JSON-SCHEMA',
    ],
    stack: ['TypeScript', 'Next.js', 'NestJS', 'React Native', 'PostgreSQL', 'MongoDB', 'AWS', 'Docker', 'Kubernetes'],
    accentColor: '#f97316',
    logoSrc: '/images/cards/upwork.png',
  },
  {
    id: 'dev-weekends',
    company: 'Dev Weekends',
    role: 'Mentor (Non-Profit Community)',
    period: '2024 — Present',
    type: 'Volunteer',
    location: 'Remote',
    description:
      'Mentoring in a 20K+ no-profit engineering community focused on DSA and full-stack growth. Supporting learners through structured fellowship guidance and live teaching sessions.',
    responsibilities: [
      'Delivered multiple community sessions on DSA patterns, problem solving, and full-stack fundamentals',
      'Mentored 40+ fellows each year with consistent guidance on learning plans and interview preparation',
      'Collaborated with a 50+ volunteer team running community initiatives without any paid model',
      'Contributed to a learner-first support culture that helps students become job-ready engineers',
    ],
    stack: ['DSA', 'Full-Stack Mentorship', 'Community Leadership', 'Career Guidance'],
    accentColor: '#0ea5e9',
    logoSrc: '/images/cards/devweekends.png',
  },
]

export default function Experience() {
  const [active, setActive] = useState('ridenear')
  const current = experiences.find((e) => e.id === active)

  return (
    <section className="section experience" id="experience">
      <div className="experience__wrapper" style={{ position: 'relative' }}>
        <Meteors
          speed={0.1}
          size={20}
          amount={2}
          colorLightmode="#2978b5"
          colorDarkmode="white"
          fallingSide="left"
        />

        <h2 className="section__title experience__heading">Work Experience</h2>

        <div className="experience__layout">
          {/* Left — tab selector */}
          <aside className="experience__tabs">
            {experiences.map((exp) => (
              <button
                key={exp.id}
                className={`experience__tab ${active === exp.id ? 'experience__tab--active' : ''}`}
                onClick={() => {
                  setActive(exp.id)
                  logExperienceTabClick(exp.id, exp.company)
                }}
                style={active === exp.id ? { '--accent': exp.accentColor } : {}}
              >
                <span className="experience__tab-logo">
                  <img src={exp.logoSrc} alt={`${exp.company} logo`} />
                </span>
                <div className="experience__tab-info">
                  <span className="experience__tab-company">{exp.company}</span>
                  <span className="experience__tab-role">{exp.role}</span>
                </div>
              </button>
            ))}
          </aside>

          {/* Right — detail panel */}
          <div className="experience__panel" key={active}>
            <div className="experience__panel-header">
              <div className="experience__panel-title-row">
                <span className="experience__panel-logo">
                  <img src={current.logoSrc} alt={`${current.company} logo`} />
                </span>
                <div>
                  <h3 className="experience__panel-company">{current.company}</h3>
                  <p className="experience__panel-role">{current.role}</p>
                </div>
              </div>
              <div className="experience__panel-meta">
                <span className="experience__meta-badge">{current.type}</span>
                <span className="experience__meta-period">{current.period}</span>
                <span className="experience__meta-location">{current.location}</span>
              </div>
            </div>

            <p className="experience__panel-desc">{current.description}</p>

            <div className="experience__responsibilities">
              {current.responsibilities.map((r, i) => (
                <div key={i} className="experience__responsibility">
                  <span className="experience__responsibility-dot" style={{ background: current.accentColor }} />
                  <span>{r}</span>
                </div>
              ))}
            </div>

            <div className="experience__stack">
              {current.stack.map((tech) => (
                <span
                  key={tech}
                  className="experience__stack-tag"
                  style={{ '--accent': current.accentColor }}
                >
                  {tech}
                </span>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  )
}
